/**
 * Marker placement, ported from native `TaskMapView`.
 */
import type { GeoPoint } from './bucketSettings';

/** ~18 m: tasks captured in one room get near-identical fixes and would stack. */
const OVERLAP_SPREAD_RADIUS_DEG = 0.00016;
/** Coordinates equal to ~11 m (4 decimals) count as the same spot. */
const OVERLAP_KEY_PRECISION = 4;
const METERS_PER_DEG_LAT = 111000;

export interface MapEntry<T> {
  item: T;
  position: { lat: number; lng: number };
}

/** One marker per item; same-spot stacks fan out in a small circle. Display only. */
export function spreadOverlapping<T extends { location: GeoPoint }>(items: T[]): MapEntry<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = `${item.location.latitude.toFixed(OVERLAP_KEY_PRECISION)}|${item.location.longitude.toFixed(OVERLAP_KEY_PRECISION)}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  const entries: MapEntry<T>[] = [];
  for (const group of groups.values()) {
    group.forEach((item, i) => {
      const { latitude, longitude } = item.location;
      if (group.length === 1) {
        entries.push({ item, position: { lat: latitude, lng: longitude } });
        return;
      }
      const angle = (2 * Math.PI * i) / group.length - Math.PI / 2;
      const latScale = Math.max(0.2, Math.cos((latitude * Math.PI) / 180));
      entries.push({
        item,
        position: {
          lat: latitude + OVERLAP_SPREAD_RADIUS_DEG * Math.sin(angle),
          lng: longitude + (OVERLAP_SPREAD_RADIUS_DEG * Math.cos(angle)) / latScale,
        },
      });
    });
  }
  return entries;
}

/** The circle's four cardinal edges, so a fit keeps the whole geofence in view. */
export function geofenceEdges(
  center: GeoPoint,
  radius: number,
): Array<{ lat: number; lng: number }> {
  if (!radius) return [];
  const latDelta = radius / METERS_PER_DEG_LAT;
  const lngDelta = radius / (METERS_PER_DEG_LAT * Math.cos((center.latitude * Math.PI) / 180));
  return [
    { lat: center.latitude + latDelta, lng: center.longitude },
    { lat: center.latitude - latDelta, lng: center.longitude },
    { lat: center.latitude, lng: center.longitude + lngDelta },
    { lat: center.latitude, lng: center.longitude - lngDelta },
  ];
}
